import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Container, Spinner, Row, Col, Input, InputGroup
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { MapPin, Calendar, Clock, Activity, User } from 'react-feather';

interface BystanderStatusLogData {
    id: number;
    bystander_id: number;
    status: number;
    previous_status: number | null;
    latitude: number | null;
    longitude: number | null;
    trigger_source: string;
    remarks: string | null;
    created_at: string;
    bystander?: {
        id: number;
        name: string;
        phone_number: string;
        email: string;
        profile_pic?: string;
    };
}

export default function BystanderStatusLogs() {
    const [data, setData] = useState<BystanderStatusLogData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm, statusFilter);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, statusFilter, currentPage, perPage, sortBy, sortDir]);

    const fetchData = async (
        page: number,
        size: number,
        sort: string,
        direction: string,
        search: string,
        status: string
    ) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`bystander/status_logs?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search,
                status: status !== "all" ? status : undefined,
            });

            if (response.status) {
                setData(response.data || []);
                setTotalRows(response.total || 0);
            } else {
                toast.error("Failed to load bystander status logs");
            }
        } catch (error) {
            toast.error("Failed to load bystander status logs");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || "created_at");
        setSortDir(sortDirection);
    };

    const getStatusBadge = (status: number) => {
        switch (Number(status)) {
            case 1:
                return (
                    <span
                        style={{
                            backgroundColor: "#f0fdf4",
                            color: "#16a34a",
                            border: "1px solid #bbf7d0",
                            borderRadius: "14px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px"
                        }}
                    >
                        <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#16a34a" }} />
                        Online
                    </span>
                );
            case 2:
                return (
                    <span
                        style={{
                            backgroundColor: "#f8fafc",
                            color: "#475569",
                            border: "1px solid #cbd5e1",
                            borderRadius: "14px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px"
                        }}
                    >
                        <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#475569" }} />
                        On Duty
                    </span>
                );
            case 0:
            default:
                return (
                    <span
                        style={{
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                            borderRadius: "14px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px"
                        }}
                    >
                        <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#dc2626" }} />
                        Offline
                    </span>
                );
        }
    };

    const formatTriggerSource = (source: string) => {
        switch (source) {
            case 'manual_toggle':
                return (
                    <span style={{
                        backgroundColor: '#f8fafc',
                        color: '#334155',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        📱 Manual Toggle
                    </span>
                );
            case 'service_start':
                return (
                    <span style={{
                        backgroundColor: '#fef3c7',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        ⚡ Service Started
                    </span>
                );
            case 'service_complete':
                return (
                    <span style={{
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        ✓ Service Completed
                    </span>
                );
            case 'gps_disabled':
                return (
                    <span style={{
                        backgroundColor: '#fee2e2',
                        color: '#b91c1c',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        ⚠️ GPS Disabled
                    </span>
                );
            default:
                return (
                    <span style={{
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center'
                    }}>
                        {source}
                    </span>
                );
        }
    };

    const columns = [
        {
            name: "Sl.No",
            cell: (row: BystanderStatusLogData, index: number) => (
                <span className="fw-semibold text-muted">{index + 1 + (currentPage - 1) * perPage}</span>
            ),
            sortable: false,
            width: "8%",
        },
        {
            name: "Bystander Name",
            selector: (row: BystanderStatusLogData) => row.bystander?.name || "Unknown",
            sortable: true,
            sortField: "bystander_name",
            wrap: true,
            cell: (row: BystanderStatusLogData) => (
                <div className="d-flex flex-column py-1">
                    <span className="fw-bold text-dark">{row.bystander?.name || "Unknown"}</span>
                    {row.bystander?.phone_number && (
                        <span className="text-muted small" style={{ fontSize: '11px' }}>{row.bystander.phone_number}</span>
                    )}
                </div>
            ),
            width: "18%",
        },
        {
            name: "Date and Time",
            selector: (row: BystanderStatusLogData) => row.created_at,
            sortable: true,
            sortField: "created_at",
            cell: (row: BystanderStatusLogData) => (
                <div className="d-flex flex-column">
                    <span className="fw-bold">{new Date(row.created_at).toLocaleDateString()}</span>
                    <span className="small text-muted">{new Date(row.created_at).toLocaleTimeString()}</span>
                </div>
            ),
            width: "18%",
        },
        {
            name: "Current Status",
            selector: (row: BystanderStatusLogData) => row.status,
            sortable: true,
            sortField: "status",
            cell: (row: BystanderStatusLogData) => getStatusBadge(row.status),
            width: "16%",
        },
        {
            name: "Trigger Event",
            selector: (row: BystanderStatusLogData) => row.trigger_source,
            sortable: true,
            sortField: "trigger_source",
            cell: (row: BystanderStatusLogData) => (
                <div className="d-flex align-items-center py-1">
                    {formatTriggerSource(row.trigger_source)}
                </div>
            ),
            width: "20%",
        },
        {
            name: "Location Coordinates",
            selector: (row: BystanderStatusLogData) => `${row.latitude}, ${row.longitude}`,
            sortable: true,
            sortField: "latitude",
            cell: (row: BystanderStatusLogData) => (
                row.latitude && row.longitude ? (
                    <div className="d-flex flex-column gap-1 align-items-start py-1">
                        <div className="d-flex flex-column">
                            <span className="small fw-bold" style={{ fontSize: '11px', color: '#4b5563' }}>Lat: {Number(row.latitude).toFixed(6)}</span>
                            <span className="small fw-bold" style={{ fontSize: '11px', color: '#4b5563' }}>Lng: {Number(row.longitude).toFixed(6)}</span>
                        </div>
                        <button
                            onClick={() => window.open(`https://www.google.com/maps?q=${row.latitude},${row.longitude}`, '_blank')}
                            style={{
                                border: '1px solid #0d9488',
                                color: '#0d9488',
                                backgroundColor: '#f0fdfa',
                                borderRadius: '6px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s',
                                marginTop: '2px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d9488';
                                e.currentTarget.style.color = '#ffffff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#f0fdfa';
                                e.currentTarget.style.color = '#0d9488';
                            }}
                        >
                            <MapPin size={11} /> View on Map
                        </button>
                    </div>
                ) : (
                    <span className="text-muted small">-</span>
                )
            ),
            width: "20%",
        },

    ];
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className="page-body">
            <ToastContainer />
            <Breadcrumbs pageTitle="Bystander Duty & Status Logs" parent="Bystander Management" title="Duty Logs" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                {/* Search & Status Filter Row */}
                                <Row className="mb-3 justify-content-end">
                                    <Col md={4} lg={3}>
                                        <InputGroup className="shadow-sm border-0">
                                            <Input
                                                placeholder="Search by bystander..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="form-control-sm"
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </InputGroup>
                                    </Col>
                                </Row>

                                {/* DataTable with Backend Pagination & Sorting */}
                                <Col sm="12">
                                    <Card className="shadow-sm border-0">


                                        <CardBody className="p-0">
                                            <div className="table-responsive">
                                                <DataTable
                                                    columns={columns}
                                                    data={data}
                                                    progressPending={loading}
                                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                                    noDataComponent={
                                                        <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                                            <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                                                <CardBody>
                                                                    <h6 className="fw-semibold mb-1">No Status Logs</h6>
                                                                    <small className="text-muted">No instances of status logs found.</small>
                                                                </CardBody>
                                                            </Card>
                                                        </div>
                                                    }
                                                    pagination
                                                    paginationServer
                                                    paginationTotalRows={totalRows}
                                                    paginationDefaultPage={currentPage}
                                                    paginationPerPage={perPage}
                                                    onChangePage={handlePageChange}
                                                    onChangeRowsPerPage={handlePerRowsChange}
                                                    sortServer
                                                    onSort={handleSort}
                                                    customStyles={customStyles}
                                                    highlightOnHover
                                                    striped
                                                />
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div >
    );
}
